import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { PlateMindLogo } from "@/components/PlateMindLogo";

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSent(true);
        toast.success("Reset link sent!");
      } else {
        toast.error(data.detail || "Failed to send reset link");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="p-4 md:p-6">
          <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-secondary transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <Card className="w-full max-w-md rounded-3xl border border-orange-100/50 shadow-soft-lift">
            <CardHeader className="text-center space-y-4 pb-2">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <CardTitle className="font-heading text-2xl font-bold text-secondary">Check Your Email</CardTitle>
              <CardDescription className="text-muted-foreground">
                We've sent a password reset link to <span className="font-medium text-secondary">{email}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  The link will expire in 1 hour. If you don't see the email, check your spam folder.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSent(false)}
                  className="rounded-full"
                  data-testid="try-another-email-btn"
                >
                  Try another email
                </Button>
              </div>
              <div className="text-center">
                <Link to="/login" className="text-sm text-primary font-medium hover:underline">
                  Return to Login
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="p-4 md:p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-secondary transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Login</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md rounded-3xl border border-orange-100/50 shadow-soft-lift">
          <CardHeader className="text-center space-y-2 pb-2">
            <div className="mx-auto mb-2">
              <PlateMindLogo size={56} />
            </div>
            <CardTitle className="font-heading text-2xl font-bold text-secondary">Forgot Password?</CardTitle>
            <CardDescription className="text-muted-foreground">
              Enter your email and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-secondary font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-email-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-xl border-2 border-orange-50 focus:border-primary py-5"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary hover:shadow-button-primary-hover transition-all"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Remember your password?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  Log in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ForgotPassword;
